import { createContext, ReactNode, useContext, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import createImportTableStore, { ImportTableStateType, ImportTableStoreType } from '../../store/importTableStore';
import { ImportDescriptorIdType, ReportIdType } from '../../../../types';
import { NUMBER_DESCRIPTOR_DATA_TYPE } from '../../constants';

const ImportTableContext = createContext<ReturnType<typeof createImportTableStore> | null>(null);

type ImportDescriptorsStoreProviderPropsType = {
    initialState: ImportTableStateType;
    children: ReactNode;
};

export function ImportTableStoreProvider({ children, initialState }: ImportDescriptorsStoreProviderPropsType) {
    const [store] = useState(() => createImportTableStore(initialState));

    return <ImportTableContext.Provider value={store}>{children}</ImportTableContext.Provider>;
}

export function useImportTableStore<T>(selector: (state: ImportTableStoreType) => T) {
    const store = useContext(ImportTableContext);
    if (!store) {
        throw new Error('useImportTableStore should be used in ImportTableStoreProvider');
    }
    return useStore(store, useShallow(selector));
}

export const useImportTableStoreDescriptorActions = () => useImportTableStore((state) => state.descriptorActions);

export const useImportTableStoreReportActions = () => useImportTableStore((state) => state.reportsActions);

export const useImportTableStoreDescriptorsMap = () => useImportTableStore((state) => state.descriptorsMap);

export const useImportTableStoreDescriptor = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) => state.descriptorsMap[descriptorId]);

export const useImportTableStoreDescriptorsEditMode = () =>
    useImportTableStore((state) => state.labelEditing && state.scaleEditing && state.typeEditing);

export const useImportTableStoreReportsEditMode = () =>
    useImportTableStore((state) => Object.values(state.reportsMap).every((report) => report.editing));

// TODO: this hook should probably be more generic returning the data type of a descriptor
// the rest should be a reusable predicate
export const useImportTableStoreIsDescriptorDataTypeNumber = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) => state.descriptorsMap[descriptorId].dataType === NUMBER_DESCRIPTOR_DATA_TYPE);

export const useImportTableStoreReportFieldValue = (descriptorId: ImportDescriptorIdType, reportId: ReportIdType) =>
    useImportTableStore((state) => state.reportsMap[reportId].rowIdToValue[descriptorId]);

export const useImportTableStoreReportsMap = () => useImportTableStore((state) => state.reportsMap);

export const useImportTableStoreFieldError = (descriptorId: ImportDescriptorIdType, reportId: ReportIdType) =>
    useImportTableStore((state) => {
        const error = { error: false, message: '' };
        if (state.reportsErrors[reportId] && state.reportsErrors[reportId][descriptorId]) {
            error.error = state.reportsErrors[reportId][descriptorId].error;
            error.message = state.reportsErrors[reportId][descriptorId].message;
        }
        return error;
    });

export const useImportTableStoreReportsErrors = () => useImportTableStore((state) => state.reportsErrors);

export const useImportTableStoreNavigationActiveCoordinates = () =>
    useImportTableStore((state) => ({
        activeRow: state.activeCoordinates.activeRow,
        activeCol: state.activeCoordinates.activeCol,
    }));

export const useImportTableStoreCoordinatesActions = () => useImportTableStore((state) => state.coordinatesActions);

export const useImportTableStoreIsCellActive = (cellRow: number, cellCol: number) =>
    useImportTableStore(
        (state) => state.activeCoordinates.activeRow === cellRow && state.activeCoordinates.activeCol === cellCol
    );

/**
 * @description Retrieves the field values of every report for the given descriptor id
 */
export const useImportTableStoreReportsFieldValue = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) =>
        Object.values(state.reportsMap).map((report) => {
            return report.rowIdToValue[descriptorId];
        })
    );
